import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Typography, Button} from '@customink/pigment-react';
import {useHistory} from 'react-router-dom';
import {ShippingAddressDetails} from 'models/shippingAddressDetails';
import {AddressValidationSuggestion} from 'models/addressValidation';
import {
  useCreateStoreAddressMutation,
  useGetStoreAddressesQuery,
  useUpdateStoreAddressMutation,
  useValidateAddressMutation
} from '../../api/storesApi';
import AlertConfirmation from './AlertConfirmation';
import SettingDialog from './Dialog';
import ShippingDetails from './ShippingDetails';

type ShippingDetailsFormProps = {
  storeSlug: string;
};

const ShippingDetailsForm: React.FC<ShippingDetailsFormProps> = ({
  storeSlug
}: ShippingDetailsFormProps): ReactElement => {
  const defaultValues: ShippingAddressDetails = {
    shippingFirstName: '',
    shippingLastName: '',
    shippingAddressOne: '',
    shippingAddressTwo: '',
    shippingCity: '',
    shippingState: '',
    shippingCountry: 'US',
    shippingPhoneNumber: '',
    shippingPostalCode: ''
  };

  const [
    triggerUpdateStoreAddressMutation,
    {isError: isUpdateStoreAddressError, isLoading: isUpdateStoreAddressLoading}
  ] = useUpdateStoreAddressMutation();

  const [
    triggerCreateStoreAddressMutation,
    {isError: isCreateStoreAddressError, isLoading: isCreateStoreAddressLoading}
  ] = useCreateStoreAddressMutation();

  const [
    triggerValidateAddressMutation,
    {isLoading: isValidateAddressLoading}
  ] = useValidateAddressMutation();

  const {data: storeAddressesData = [], isLoading: isStoreAddressesLoading} =
    useGetStoreAddressesQuery({storeSlug});

  const history = useHistory();

  const formMethods = useForm<ShippingAddressDetails>({defaultValues});

  const {
    reset: resetForm,
    formState: {isDirty: isFormDirty, isSubmitted: isFormSubmitted},
    getValues: getFormValues,
    handleSubmit: handleFormSubmit
  } = formMethods;

  const formValues = getFormValues();

  const unblockRef = useRef(null);

  const [showGeneralError, setShowGeneralError] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showValidateError, setShowValidateError] = useState(false);
  const [showConfirmAddress, setShowConfirmAddress] = useState(false);
  const [addressSuggestion, setAddressSuggestion] =
    useState<AddressValidationSuggestion | null>(null);
  const [currentPath, setCurrentPath] = useState('');

  const isQueryError = isUpdateStoreAddressError || isCreateStoreAddressError;

  const patchStoreAddress = async (data: ShippingAddressDetails) => {
    await triggerUpdateStoreAddressMutation({
      storeSlug,
      addressId: storeAddressesData[0].id,
      ...data
    });
    setShowSuccessAlert(!isQueryError);
  };

  const createStoreAddress = async (data: ShippingAddressDetails) => {
    await triggerCreateStoreAddressMutation({
      storeSlug,
      ...data
    });
    setShowSuccessAlert(!isQueryError);
  };

  const handleConfirmAddressValidation = async (
    data: ShippingAddressDetails
  ) => {
    const address: ShippingAddressDetails = {
      shippingFirstName: data.shippingFirstName,
      shippingLastName: data.shippingLastName,
      shippingAddressOne:
        addressSuggestion?.address1 ?? data.shippingAddressOne,
      shippingAddressTwo:
        addressSuggestion?.address2 ?? data.shippingAddressTwo,
      shippingCity: addressSuggestion?.city ?? data.shippingCity,
      shippingState: addressSuggestion?.state ?? data.shippingState,
      shippingCountry: addressSuggestion?.country ?? data.shippingCountry,
      shippingPhoneNumber: data.shippingPhoneNumber,
      shippingPostalCode:
        addressSuggestion?.postalCode ?? data.shippingPostalCode
    };
    if (storeAddressesData?.length > 0) {
      await patchStoreAddress(address);
    } else {
      await createStoreAddress(address);
    }
    setShowConfirmAddress(false);
    setShowValidateError(false);
    resetForm(address);
  };

  /* 
    Form handlers
  */

  const handleSubmit = async (addressDetails: ShippingAddressDetails) => {
    setAddressSuggestion(null);
    await triggerValidateAddressMutation({
      storeSlug,
      ...addressDetails
    }).then(response => {
      if ('error' in response || !response.data?.valid) {
        setShowValidateError(true);
      } else {
        const {
          data: {suggestion}
        } = response;

        setAddressSuggestion(suggestion);
        setShowConfirmAddress(true);
      }
    });
  };

  const handleLeaveDialogConfirm = () => {
    if (unblockRef) {
      unblockRef.current();
    }
    setShowLeaveDialog(false);
    history.push(currentPath);
  };

  const handleCloseAlert = () => setShowSuccessAlert(false);

  /* 
    useEffect hooks
  */

  // Set form default values when settings load
  useEffect(() => {
    if (storeAddressesData && storeAddressesData.length > 0) {
      const address = storeAddressesData[0];
      const newValues = {
        ...getFormValues(),
        shippingFirstName: address.firstName,
        shippingLastName: address.lastName,
        shippingAddressOne: address.address1,
        shippingAddressTwo: address.address2,
        shippingCity: address.city,
        shippingState: address.state,
        shippingCountry: address.country,
        shippingPhoneNumber: address.phoneNumber,
        shippingPostalCode: address.postalCode,
        locationName: address.location.name,
        locationEmail: address.location.email
      };
      resetForm({...newValues});
    }
  }, [storeAddressesData]);

  useEffect(() => {
    unblockRef.current = history.block((location: typeof history) => {
      if (isFormDirty && !isFormSubmitted) {
        setCurrentPath(location.pathname);
        setShowLeaveDialog(true);
        return false;
      }
      return true;
    });
    return () => unblockRef.current && unblockRef.current();
  }, [isFormSubmitted, isFormDirty]);

  useEffect(() => {
    setShowGeneralError(isUpdateStoreAddressError || isCreateStoreAddressError);
  }, [isUpdateStoreAddressError, isCreateStoreAddressError]);

  if (isStoreAddressesLoading) return null;

  return (
    <FormProvider {...formMethods}>
      {showGeneralError && (
        <Typography variant="subHeading" className="generalError">
          Something went wrong. Please try again later.
        </Typography>
      )}
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <ShippingDetails
          storeSlug={storeSlug}
          storeAddressData={storeAddressesData}
        />
        <div className="settingsActions" style={{padding: 0}}>
          <Button
            className="cancel-button"
            aria-label="reset"
            variant="link"
            disabled={
              !isFormDirty ||
              isUpdateStoreAddressLoading ||
              isCreateStoreAddressLoading ||
              isValidateAddressLoading
            }
            onClick={() => {
              setShowGeneralError(false);
              resetForm();
            }}>
            Cancel
          </Button>
          <Button
            className="submit-button"
            type="submit"
            disabled={
              !isFormDirty ||
              isUpdateStoreAddressLoading ||
              isCreateStoreAddressLoading ||
              isValidateAddressLoading
            }>
            Save
          </Button>
        </div>
      </form>
      <SettingDialog
        open={showConfirmAddress}
        title="We have validated this address"
        descriptionContent={`${
          addressSuggestion?.address1 ?? formValues.shippingAddressOne
        } ${addressSuggestion?.address2 ?? formValues.shippingAddressTwo} ${
          addressSuggestion?.postalCode ?? formValues.shippingPostalCode
        }, ${addressSuggestion?.city ?? formValues.shippingCity}, ${
          addressSuggestion?.state ?? formValues.shippingState
        }, ${addressSuggestion?.country ?? formValues.shippingCountry}`}
        confirmText="Save Address"
        closeText="Back to editing"
        handleConfirm={() => handleConfirmAddressValidation(getFormValues())}
        handleClose={() => setShowConfirmAddress(false)}
      />
      <SettingDialog
        open={showValidateError}
        title="Error validating address"
        alertContent="We could not validate this address, are you sure you want to save it?"
        confirmText="Save Address"
        closeText="Back to editing"
        handleConfirm={() => handleConfirmAddressValidation(getFormValues())}
        handleClose={() => setShowValidateError(false)}
      />
      <SettingDialog
        open={showLeaveDialog}
        title="Are you sure you want to leave without saving?"
        alertContent="You have unsaved changes. If you continue without saving, your recent changes will be lost."
        closeText="Go Back"
        confirmText="Continue Without Saving"
        handleConfirm={handleLeaveDialogConfirm}
        handleClose={() => setShowLeaveDialog(false)}
      />
      <AlertConfirmation
        showSuccessAlert={showSuccessAlert}
        onCloseAlert={handleCloseAlert}
        groupOrderAlert={{
          message: 'Your shipping details have been saved.'
        }}
      />
    </FormProvider>
  );
};

export default ShippingDetailsForm;
